<template>
    <div class="touchpoints-view card">
        <div class="card-header row">
            <div class="d-xxl-flex d-block justify-content-between touchpoints-header-controls">
                <div class="d-flex gap-2 flex-wrap touchpoints-action-buttons mb-3">
                    <div v-if="view_mode !== 'calendar'" class="flex-grow-1 position-relative" >
                        <input
                            type="text"
                            v-model="filters.search"
                            @input="handleSearchInput"
                            placeholder="Search"
                            class="form-control search-box"
                            aria-label="Search"
                            style="height: 30px; min-height: 30px;border-radius: 15px;"
                        >
                        <i class="fa fa-search search-placeholder-icon"></i>
                    </div>
                    
                    <button class="btn btn-secondary btn-sm dropdown-toggle dropdown-toggle-no-btn px-3" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                        <img src="/img/icons/avid-icons/filter-funnel-01.svg" class="icon me-1" alt="">
                        Filters <span>({{ activeFilterCount }})</span>
                    </button>
                    <div class="dropdown-menu dropdown-menu-primary p-3">
                        <p>Filter by Touchpoint Type:</p>
                        <accordion-multi-select
                            v-if="assetTypeOptionsForFilter.length > 0"
                            :model-value="selectedAssetTypeOptions"
                            :options="assetTypeOptionsForFilter"
                            field_name="touchpoint_type_filter"
                            @update:modelValue="handleAssetTypeFilterUpdate"
                            class="mb-3"
                            style="max-width: 400px;"
                        />

                        <hr>

                        <p>Filter by Status:</p>
                        <select class="form-select mb-3" v-model="filters.status" @change="handleFilterChange" :disabled="availableStatuses.length === 0" aria-label="Status">
                            <option value="">{{ availableStatuses.length > 0 ? 'All Statuses' : 'No statuses available' }}</option>
                            <option v-for="status in availableStatuses" :key="status" :value="status">
                                {{ formatStatus(status) }}
                            </option>
                        </select>

                        <hr>    

                        <p>Filter by Audience:</p>
                        <select class="form-select" v-model="filters.audience" @change="handleFilterChange" :disabled="availableAudiences.length === 0" aria-label="Audience">
                            <option value="">{{ availableAudiences.length > 0 ? 'All Audiences' : 'No audiences available' }}</option>
                            <option v-for="audience in availableAudiences" :key="audience.id" :value="audience.id">
                                {{ audience.name }}
                            </option>
                        </select>
                    </div>

                    <button v-if="view_mode !== 'calendar'" ref="groupByButton" class="btn btn-secondary btn-sm dropdown-toggle dropdown-toggle-no-btn px-3" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                        <img src="/img/icons/avid-icons/group.svg" class="icon me-1" alt="">
                        Group By
                    </button>
                    <div class="dropdown-menu dropdown-menu-primary p-3">
                        <a class="dropdown-item" 
                           :class="{ 'active': groupBy === 'type' }"
                           href="#"
                           @click.prevent="setGroupBy('type')">
                           <span class="ms-2">Type</span>
                        </a>
                        <a class="dropdown-item" 
                           :class="{ 'active': groupBy === 'launch_date' }"
                           href="#"
                           @click.prevent="setGroupBy('launch_date')">
                            <span class="ms-2">Launch Date</span>
                        </a>
                        <a class="dropdown-item" 
                           :class="{ 'active': groupBy === 'audience' }"
                           href="#"
                           @click.prevent="setGroupBy('audience')">
                            <span class="ms-2">Audience</span>
                        </a>
                    </div>

                    <button v-if="view_mode === 'grid'" class="btn btn-secondary btn-sm px-3" @click="toggleAllCards">
                        <img v-if="allCardsCollapsed" src="/img/icons/avid-icons/collapse.svg" class="icon me-1" alt=""></img>
                        <img v-else src="/img/icons/avid-icons/expand.svg" class="icon me-1" alt=""></img>
                        {{ allCardsCollapsed ? 'Expand All' : 'Collapse All' }}
                    </button>

                    <button v-if="view_mode !== 'calendar'" class="btn btn-secondary btn-sm px-3" @click="toggleSelectAll">
                        <img v-if="anyCardsSelected" src="/img/icons/avid-icons/x-circle.svg" class="icon me-1" alt=""></img>
                        <img v-else src="/img/icons/avid-icons/check-circle.svg" class="icon me-1" alt=""></img>
                        {{ anyCardsSelected ? 'Deselect All' : 'Select All' }}
                    </button>

                    <button v-if="view_mode !== 'calendar' && !anyCardsSelected && canEdit('playbooks') && exports.length > 0" class="btn btn-secondary btn-sm px-3" @click="importContent">
                        <img src="/img/icons/avid-icons/import.svg" class="icon me-1" alt=""> Import
                    </button>
                    <div v-else-if="view_mode !== 'calendar' && !anyCardsSelected && !canEdit('playbooks') && exports.length > 0" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button class="btn btn-secondary btn-sm px-3" :disabled="true">
                            <img src="/img/icons/avid-icons/import.svg" class="icon me-1" alt=""> Import
                        </button>
                    </div>

                    <button v-if="view_mode !== 'calendar' && anyCardsSelected && canExport('playbooks')" class="btn btn-secondary btn-sm px-3" @click="reviewSelected">
                        <img src="/img/icons/avid-icons/download.svg" class="icon me-1" alt=""> Review
                    </button>
                    <div v-else-if="view_mode !== 'calendar' && anyCardsSelected && !canExport('playbooks')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button class="btn btn-secondary btn-sm px-3" :disabled="true">
                            <img src="/img/icons/avid-icons/download.svg" class="icon me-1" alt=""> Review
                        </button>
                    </div>

                    <button v-if="canEdit('playbooks')" class="btn btn-secondary btn-sm px-3" @click="openCreateModal">
                        <img src="/img/icons/avid-icons/plus-circle.svg" class="icon me-1" alt=""> Add
                    </button>
                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button class="btn btn-secondary btn-sm px-3" :disabled="true">
                            <img src="/img/icons/avid-icons/plus-circle.svg" class="icon me-1" alt=""> Add
                        </button>
                    </div>

                    <button v-if="view_mode !== 'calendar' && anyCardsSelected && canEdit('playbooks')" class="btn btn-warning btn-sm px-3" @click="deleteSelected">
                        <img src="/img/icons/avid-icons/trash.svg" class="icon me-1" alt="">
                        Delete
                    </button>
                    <div v-else-if="view_mode !== 'calendar' && anyCardsSelected && !canEdit('playbooks')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                        <button class="btn btn-warning btn-sm px-3" :disabled="true">
                            <img src="/img/icons/avid-icons/trash.svg" class="icon me-1" alt="">
                            Delete
                        </button>
                    </div>
                </div>                
                               
                <div class="d-flex">
                    <nav class="navbar navbar-expand tertiary-tab-bar">
                        <ul class="navbar-nav">
                            <li class="nav-item">
                                <a class="nav-link d-flex flex-nowrap tertiary-tab"
                                    :class="{ 'tertiary-tab-active': view_mode === 'grid' }"
                                    href="#"
                                    @click.prevent="setViewMode('grid')"
                                    data-bs-toggle="tooltip"
                                    title="Grid view"
                                >
                                    <span><img src="/img/icons/avid-icons/grid-view.svg" class="icon" alt=""></span>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link d-flex flex-nowrap tertiary-tab"
                                    :class="{ 'tertiary-tab-active': view_mode === 'list' }"
                                    href="#"
                                    @click.prevent="setViewMode('list')"
                                    data-bs-toggle="tooltip"
                                    title="List view"
                                >
                                    <span><img src="/img/icons/avid-icons/list-view.svg" class="icon" alt=""></span>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link d-flex flex-nowrap tertiary-tab"
                                    :class="{ 'tertiary-tab-active': view_mode === 'calendar' }"
                                    href="#"
                                    @click.prevent="setViewMode('calendar')"
                                    data-bs-toggle="tooltip"
                                    title="Calendar view"
                                >
                                    <span><img src="/img/icons/avid-icons/calendar.svg" class="icon" alt=""></span>
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
        <div class="card-body">
            <!-- Header Section -->
            <div class="col-12 mb-5">
              <h3 class="d-flex align-items-center">
                <span v-if="!editingName && canEdit('playbooks')" @click="startEditName" class="editable-field" :class="{ 'saving': savingName }" data-bs-toggle="tooltip" title="Click to edit">
                  {{ playbook.name }}
                </span>
                <span v-else-if="!editingName">
                  {{ playbook.name }}
                </span>
                <input 
                  v-else
                  type="text" 
                  class="form-control d-inline-block" 
                  style="max-width: 400px;"
                  v-model="tempName"
                  @blur="saveName"
                  @keyup.enter="saveName"
                  @keyup.esc="cancelEditName"
                  ref="nameInput" aria-label="Temp Name">
                <span class="badge ms-2 px-3" :class="{ 'badge-draft': playbook.status === 'draft', 'badge-active': playbook.status === 'active'}">{{ propercase(playbook.status) }}</span>
              </h3>
              <p v-if="playbook.start_date || playbook.end_date || editingStartDate || editingEndDate" class="mb-2">
                <!-- <img src="/img/icons/avid-icons/calendar.svg" class="icon" style="width: 16px; height: 16px;" alt=""> -->
                Date: 
                <span v-if="!editingStartDate && canEdit('playbooks')" @click="startEditStartDate" class="editable-field" :class="{ 'saving': savingDate === 'start' }" data-bs-toggle="tooltip" title="Click to edit">
                  {{ playbook.start_date ? formatDateForDisplay(playbook.start_date) : 'Start date' }}
                </span>
                <span v-else-if="!editingStartDate">
                  {{ playbook.start_date ? formatDateForDisplay(playbook.start_date) : 'Start date' }}
                </span>
                <span v-else class="d-inline-block" style="max-width: 150px;">
                  <DatepickerUtc 
                    v-model="tempStartDate"
                    @update:modelValue="handleStartDateChange"
                    placeholder="Select start date"
                    input-format="M/d/yyyy"
                    input-class="form-control form-control-sm"
                  />
                </span>
                to 
                <span v-if="!editingEndDate && canEdit('playbooks')" @click="startEditEndDate" class="editable-field" :class="{ 'saving': savingDate === 'end' }" data-bs-toggle="tooltip" title="Click to edit">
                  {{ playbook.end_date ? formatDateForDisplay(playbook.end_date) : 'End date' }}
                </span>
                <span v-else-if="!editingEndDate">
                  {{ playbook.end_date ? formatDateForDisplay(playbook.end_date) : 'End date' }}
                </span>
                <span v-else class="d-inline-block" style="max-width: 150px;">
                  <DatepickerUtc 
                    v-model="tempEndDate"
                    @update:modelValue="handleEndDateChange"
                    placeholder="Select end date"
                    input-format="M/d/yyyy"
                    input-class="form-control form-control-sm"
                  />
                </span>
              </p>
              <div v-if="!editingDescription && canEdit('playbooks')" @click="startEditDescription" class="editable-field mb-0 p-0 col-md-10" :class="{ 'saving': savingDescription }" data-bs-toggle="tooltip" title="Click to edit">
                <p class="mb-0">
                  <span v-if="playbook.description">
                    {{ displayDescription }}
                    <a v-if="isDescriptionLong && !showFullDescription" @click.stop="toggleDescription" class="text-primary text-decoration-none ms-1">More...</a>
                    <a v-else-if="isDescriptionLong && showFullDescription" @click.stop="toggleDescription" class="text-primary text-decoration-none ms-1">Less</a>
                  </span>
                  <em v-else>Click to add description</em>
                </p>
              </div>
              <div v-else-if="!editingDescription" class="mb-0 p-0 col-md-10">
                <p class="mb-0">
                  <span v-if="playbook.description">
                    {{ displayDescription }}
                    <a v-if="isDescriptionLong && !showFullDescription" @click.stop="toggleDescription" class="text-primary text-decoration-none ms-1">More...</a>
                    <a v-else-if="isDescriptionLong && showFullDescription" @click.stop="toggleDescription" class="text-primary text-decoration-none ms-1">Less</a>
                  </span>
                  <em v-else>Click to add description</em>
                </p>
              </div>
              <textarea
                v-else
                class="form-control"
                rows="3"
                v-model="tempDescription"
                @blur="saveDescription"
                @keyup.esc="cancelEditDescription"
                ref="descriptionTextarea"
              ></textarea>
            </div>

            <h3 class="mb-3" v-if="view_mode !== 'calendar'">Touchpoints <span class="badge">{{ touchpointCount }}</span></h3>

            <!-- Grid View -->
            <div v-if="view_mode === 'grid'">
                <!-- No Touchpoints Message -->
                <div v-if="touchpointCount === 0" class="text-center py-5">
                    <h5>No touchpoints found</h5>
                    <p v-if="activeFilterCount > 0">Try adjusting your filters to see more results.</p>
                    <p v-else>There are no touchpoints in this play yet.</p>
                </div>

                <!-- Touchpoints Grid Layout -->
                <touchpoints-grid-view 
                    ref="gridView" 
                    :touchpoints="filteredTouchpoints" 
                    :concepts="concepts" 
                    :selected-asset-ids="selectedAssetIds"
                    :group-by="groupBy"
                    @cards-state-changed="updateCardsState" 
                    @review-single-touchpoint="reviewSingleTouchpoint">
                </touchpoints-grid-view>
            </div>
            
            <!-- List View -->
            <div v-else-if="view_mode === 'list'">
                <touchpoints-table-view 
                    ref="listView"
                    :touchpoints="filteredTouchpoints" 
                    :concepts="concepts"
                    :sort-key="sort_key"
                    :sort-order="sort_order"
                    :active-filter-count="activeFilterCount"
                    :selected-asset-ids="selectedAssetIds"
                    :group-by="groupBy"
                    @sort-by-key="sortByKey"
                    @cards-state-changed="updateCardsState"
                    @review-single-touchpoint="reviewSingleTouchpoint">
                </touchpoints-table-view>
            </div>
            
            <!-- Touchpoints Calendar View -->
            <div v-else-if="view_mode === 'calendar'">
                <touchpoints-calendar-view
                    ref="calendarView"
                    class="calendar-view-wrapper"
                    :asset-types="assetTypesForCalendar" 
                    :start_date="playbook?.start_date"
                    :end_date="playbook?.end_date" 
                    :playbook-assets="filteredTouchpoints"
                    :asset-type-filter="calendarLegendFilters"
                    :dropdown-asset-type-filter="filters.asset_type"
                    @update-days="handleCalendarUpdate"
                    @filter-asset-type="handleAssetTypeFilterChange"
                    @update-dropdown-filter="handleDropdownFilterUpdate"
                    @review-single-touchpoint="reviewSingleTouchpoint">
                </touchpoints-calendar-view>
            </div>
        </div>
        
        <!-- Import History Modal -->
        <import-history-modal
            :histories="local_exports"
            :playbook="playbook"
            :client="client"
            @refresh-history="refreshHistory"
            ref="importHistoryModal"
        />

        <!-- Create Touchpoint Modal Component -->
        <create-touchpoint-modal
            :show="createModal.show"
            :asset-types="assetTypes"
            :destination-options="destinationOptions"
            :client="client"
            :playbook="playbook"
            @create-touchpoint="handleCreateTouchpoint"
            @close="closeCreateModal"
        />
        
    </div>
</template>

<script>
import permissionsMixin from '../../mixins/permissions';
import filtersMixin from '../../mixins/filters';
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import { useNotifications } from '../../composables/useNotifications';
import ImportHistoryModal from './ImportHistoryModal.vue';
import AccordionSelect from '../components/AccordionSelect.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import TouchpointsTableView from './TouchpointsTableView.vue';
import TouchpointsCalendarView from './TouchpointsCalendarView.vue';
import TouchpointContentCard from './TouchpointContentCard.vue';
import CreateTouchpointModal from './CreateTouchpointModal.vue';
import AssetTypeInfoModal from './AssetTypeInfoModal.vue';
import EditTouchpointModal from './EditTouchpointModal.vue';
import statsigService from '../../services/statsig';
import debounce from 'lodash.debounce';

export default {
    name: 'TouchpointsView',
    emits: ['cards-state-changed', 'review-single-touchpoint'],
    components: {
        ImportHistoryModal,
        AccordionSelect,
        DatepickerUtc,
        TouchpointsTableView,
        TouchpointsCalendarView,
        TouchpointContentCard,
        CreateTouchpointModal,
        AssetTypeInfoModal,
        EditTouchpointModal,
    },
    mixins: [permissionsMixin, filtersMixin],
    props: {
        exports: {
            type: Array,
            default: () => []
        },
        concepts: {
            type: Array,
            default: () => []
        },
        assetTypes: {
            type: Array,
            default: () => []
        },
        selectedAssetIds: {
            type: Array,
            default: () => []
        }
    },
    setup() {
        const playbookStore = usePlaybookStore();
        const clientStore = useClientStore();
        const { showNotification, showProgressNotification } = useNotifications();
        return {
            playbookStore,
            clientStore,
            showNotification,
            showProgressNotification
        };
    },
    data() {
        return {
            view_mode: 'grid',
            cardsStateKey: 0, // Used to force reactivity
            sort_key: '', // empty string means no explicit sort - preserve primary-first order
            sort_order: 'asc',
            groupBy: '', // Group by option: 'type', 'launch_date', 'audience', or ''
            filters: {
                search: '',
                status: '',
                asset_type: [], // Array of selected asset type IDs
                audience: ''
            },
            calendarLegendFilters: [], // Array of asset type IDs filtered out by legend clicks
            assetTypeFilterUserInteracted: false, // Track if user has interacted with the filter
            filteredTouchpoints: [],
            local_exports: [],
            // Add Touchpoint modal state
            createModal: {
                show: false
            },
            // Edit state for playbook fields
            editingName: false,
            editingDescription: false,
            editingStartDate: false,
            editingEndDate: false,
            tempName: '',
            tempDescription: '',
            tempStartDate: null,
            tempEndDate: null,
            originalStartDate: null,
            originalEndDate: null,
            savingName: false,
            savingDescription: false,
            savingDate: null, // 'start', 'end', or null
            showFullDescription: false
        };
    },
    computed: {
        playbook() {
            return this.playbookStore.getSelectedPlaybook;
        },
        client() {
            return this.clientStore.getClient;
        },
        touchpointCount() {
            return this.filteredTouchpoints?.length ?? 0;
        },
        allCardsCollapsed() {
            // Access cardsStateKey to make this reactive
            this.cardsStateKey;
            // List view doesn't support collapse/expand
            if (this.view_mode === 'list') return false;
            return this.$refs.gridView?.allCardsCollapsed || false;
        },
        allCardsSelected() {
            // Access cardsStateKey to make this reactive
            this.cardsStateKey;
            // List view doesn't support selection
            if (this.view_mode === 'list') return false;
            return this.$refs.gridView?.allCardsSelected || false;
        },
        anyCardsSelected() {
            // Access cardsStateKey to make this reactive
            this.cardsStateKey;
            if (this.view_mode === 'list') {
                // Check if any touchpoints are selected in list view
                return Array.isArray(this.selectedAssetIds) && this.selectedAssetIds.length > 0;
            }
            // For grid view, check both the ref's computed property and the prop as fallback
            // This ensures selection state is correct even if the component hasn't synced yet
            if (this.$refs.gridView?.anyCardsSelected) {
                return true;
            }
            return Array.isArray(this.selectedAssetIds) && this.selectedAssetIds.length > 0;
        },
        activeFilterCount() {
            let count = 0;
            if (this.filters.search) count++;
            if (this.filters.status) count++;
            // Count asset_type filter only if not all types are selected
            if (Array.isArray(this.filters.asset_type) && this.filters.asset_type.length > 0 && this.filters.asset_type.length < this.availableAssetTypes.length) {
                count++;
            }
            if (this.filters.audience) count++;
            return count;
        },
        availableStatuses() {
            // Get unique calculated statuses from playbook assets
            if (!this.playbook?.assets) return [];
            
            const statuses = new Set();
            this.playbook.assets.forEach(asset => {
                // Use the same calculated status logic as in TouchpointsGridView
                if (asset.deployed_at) {
                    statuses.add('Launched');
                } else if (asset.source == null) {
                    statuses.add('Incomplete');
                } else if (asset.status == 'approved') {
                    statuses.add('Approved');
                } else if (asset.status == 'generating') {
                    statuses.add('Generating');
                } else {
                    statuses.add('Ready');
                }
            });
            return Array.from(statuses).sort();
        },
        availableAssetTypes() {
            // Get unique asset types from playbook assets
            if (!this.playbook?.assets) return [];
            
            const assetTypesMap = new Map();
            this.playbook.assets.forEach(asset => {
                if (asset.asset_type) {
                    assetTypesMap.set(asset.asset_type.id, asset.asset_type);
                }
            });
            return Array.from(assetTypesMap.values()).sort((a, b) => 
                (a.name || '').localeCompare(b.name || '')
            );
        },
        availableAudiences() {
            // Get unique audiences from playbook assets
            if (!this.playbook?.assets) return [];
            
            const audiencesMap = new Map();
            this.playbook.assets.forEach(asset => {
                if (asset.segment) {
                    audiencesMap.set(asset.segment.id, asset.segment);
                }
            });
            return Array.from(audiencesMap.values()).sort((a, b) => 
                (a.name || '').localeCompare(b.name || '')
            );
        },
        assetTypeOptions() {
            return this.assetTypes.map(at => ({
                value: at.id,
                text: at.name,
                category: '',
                // icon_class: '/img/icons/avid-icons/' + (at.icon_class || 'circle') + '.svg'
            }));
        },
        assetTypeOptionsForFilter() {
            // Format asset types for AccordionMultiSelect component
            if (!this.availableAssetTypes || this.availableAssetTypes.length === 0) {
                return [];
            }
            return this.availableAssetTypes.map(at => ({
                id: String(at.id),
                value: String(at.id),
                text: at.name,
                category: '',
                subcategory: null,
                main_category: ''
            }));
        },
        selectedAssetTypeOptions() {
            // Get selected asset type options for AccordionMultiSelect
            if (!this.availableAssetTypes || this.availableAssetTypes.length === 0) {
                return [];
            }
            
            // If no filter is set or all are selected, return all options
            if (!Array.isArray(this.filters.asset_type) || this.filters.asset_type.length === 0) {
                return this.assetTypeOptionsForFilter;
            }
            
            // Map selected IDs to option objects
            return this.filters.asset_type
                .map(id => {
                    const assetType = this.availableAssetTypes.find(at => String(at.id) === String(id));
                    if (!assetType) return null;
                    return {
                        value: String(assetType.id),
                        text: assetType.name,
                        category: '',
                        subcategory: null
                    };
                })
                .filter(option => option !== null);
        },
        destinationOptions() {
            // Return all assets currently on the playbook as destination options
            if (!this.playbook?.assets) return [];
            return this.playbook.assets.map(asset => {
                // Try to get icon_class from asset_type directly, or look it up from assetTypes
                let iconClass = '';
                if (asset.asset_type?.icon_class) {
                    iconClass = asset.asset_type.icon_class;
                } else if (asset.asset_type?.id) {
                    // Look up the icon_class from the assetTypes array
                    const assetType = this.assetTypes.find(at => at.id === asset.asset_type.id);
                    iconClass = assetType?.icon_class || '';
                }
                
                return {
                    value: asset.id,
                    text: asset.name,
                    category: ''
                };
            });
        },
        assetTypesForCalendar() {
            // Transform touchpoints (playbook assets) for calendar view
            // Calendar expects assets with launch dates and days_before_end_date
            // Use filteredTouchpoints to respect current filters
            if (!this.filteredTouchpoints || this.filteredTouchpoints.length === 0) return [];
            
            // If no end_date is set, return empty array (calendar will show message)
            if (!this.playbook?.end_date) return [];
            
            return this.filteredTouchpoints
                .filter(asset => asset.launch_date) // Only include assets with launch dates
                .map(asset => {
                    // Calculate days_before_end_date from launch_date and playbook end_date
                    const launchDate = new Date(asset.launch_date);
                    const endDate = new Date(this.playbook.end_date);
                    
                    // Calculate difference in days
                    const diffTime = endDate - launchDate;
                    const daysBeforeEndDate = Math.floor(diffTime / (1000 * 3600 * 24));
                    
                    // Return the asset in the format expected by CalendarView
                    return {
                        id: asset.asset_type?.id || asset.id,
                        name: asset.asset_type?.name || 'Unknown',
                        icon_class: asset.asset_type?.icon_class,
                        has_launch_date: true,
                        pivot: {
                            id: asset.id,
                            name: asset.name,
                            has_launch_date: true,
                            days_before_end_date: daysBeforeEndDate
                        }
                    };
                });
        },
        isDescriptionLong() {
            if (!this.playbook?.description) return false;
            const words = this.playbook.description.trim().split(/\s+/);
            return words.length > 40;
        },
        displayDescription() {
            if (!this.playbook?.description) return '';
            if (!this.isDescriptionLong || this.showFullDescription) {
                return this.playbook.description;
            }
            const words = this.playbook.description.trim().split(/\s+/);
            return words.slice(0, 40).join(' ');
        },
        groupedTouchpoints() {
            // If no grouping is selected, return null to indicate no grouping
            if (!this.groupBy) {
                return null;
            }

            // Group touchpoints by the selected option
            const groups = {};
            
            this.filteredTouchpoints.forEach(touchpoint => {
                let groupKey = '';
                let groupLabel = '';
                
                switch (this.groupBy) {
                    case 'type':
                        groupKey = touchpoint.asset_type?.id || 'no-type';
                        groupLabel = touchpoint.asset_type?.name || 'No Type';
                        break;
                    case 'launch_date':
                        if (touchpoint.launch_date) {
                            const date = new Date(touchpoint.launch_date);
                            groupKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
                            groupLabel = this.formatDateForDisplay(touchpoint.launch_date);
                        } else {
                            groupKey = 'no-date';
                            groupLabel = 'No Launch Date';
                        }
                        break;
                    case 'audience':
                        groupKey = touchpoint.segment?.id || 'no-audience';
                        groupLabel = touchpoint.segment?.name || 'No Audience';
                        break;
                    default:
                        return null;
                }
                
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        key: groupKey,
                        label: groupLabel,
                        touchpoints: []
                    };
                }
                
                groups[groupKey].touchpoints.push(touchpoint);
            });
            
            // Convert to array and sort
            return Object.values(groups).sort((a, b) => {
                // Sort by label alphabetically, except "No X" items go to the end
                if (a.label.startsWith('No ') && !b.label.startsWith('No ')) return 1;
                if (!a.label.startsWith('No ') && b.label.startsWith('No ')) return -1;
                if (this.groupBy === 'launch_date') {
                    // For dates, sort chronologically (newest first for "No date" at end)
                    if (a.key === 'no-date') return 1;
                    if (b.key === 'no-date') return -1;
                    return b.key.localeCompare(a.key); // Descending (newest first)
                }
                return a.label.localeCompare(b.label);
            });
        }
    },
    methods: {
        toggleAllCards() {
            if (this.view_mode === 'grid') {
                this.$refs.gridView?.toggleAllCards();
            }
        },
        
        toggleSelectAll() {
            if (this.view_mode === 'grid') {
                this.$refs.gridView?.toggleSelectAll();
            } else if (this.view_mode === 'list') {
                this.$refs.listView?.toggleSelectAllProgrammatic();
            }
        },
        
        reviewSelected() {
            if (this.view_mode === 'calendar') return;
            
            let selectedAssetIds = [];
            if (this.view_mode === 'grid') {
                selectedAssetIds = this.$refs.gridView?.selectedCards || [];
            } else if (this.view_mode === 'list') {
                selectedAssetIds = Array.isArray(this.selectedAssetIds) ? this.selectedAssetIds : [];
            }
            
            if (selectedAssetIds.length === 0) return;

            // Get the full asset objects for the selected asset IDs
            const selected_assets_with_name = [];
            for (let i = 0; i < selectedAssetIds.length; i++) {
                const asset = this.playbook.assets.find(a => a.id === selectedAssetIds[i]);
                if (asset) {
                    selected_assets_with_name.push({ 
                        id: asset.id, 
                        name: asset.name,
                        asset_type: asset.asset_type ? {
                            id: asset.asset_type.id,
                            name: asset.asset_type.name,
                            icon_class: asset.asset_type.icon_class
                        } : null
                    });
                }
            }

            this.startExport(selectedAssetIds, selected_assets_with_name);
        },
        
        reviewSingleTouchpoint(touchpoint) {
            // Bubble up to parent (PlayBuilder) to enter Launch flow
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        startExport(assetIds, assetsWithName) {
            // Track Google Docs export from review
            if (statsigService.getClient()) {
                statsigService.logEvent('playbook_exported_to_google_docs', null, {
                    playbook_id: this.playbook?.id,
                    playbook_type_name: this.playbook?.playbook_type?.name,
                    source: 'review',
                    asset_count: assetIds?.length || 0
                });
            }
            
            // Generate a unique export ID
            const export_id = 'export-' + Date.now();

            // Show initial progress notification
            // Backend will dispatch ProgressNotification events that will update this notification
            const notificationId = `playbook-export-${export_id}`;
            this.showProgressNotification({
                notificationId: notificationId,
                icon: '/img/icons/avid-icons/google-docs.svg',
                text: 'Your Google Doc is currently being generated. You\'ll be notified shortly when it\'s ready.',
                percentage: 0,
                status: 'working'
            });

            // Make the API call to start the export
            window.axios.post('/api/playbooks/' + this.client.url + '/generate_doc/' + this.playbook.url, {
                asset_ids: assetIds,
                export_id: export_id
            }).then(response => {
                // Update local exports when completed (handled by ProgressNotification event)
                // The backend will dispatch ProgressNotification with status 'completed' and URL
            }).catch(error => {
                // Show error notification
                this.showProgressNotification({
                    notificationId: notificationId,
                    icon: '/img/icons/avid-icons/danger.svg',
                    text: 'There was an error starting the export. Please try again.',
                    percentage: 0,
                    status: 'error'
                });
            });
        },
        
        async deleteSelected() {
            if (this.view_mode === 'grid') {
                this.$refs.gridView?.deleteSelected();
            } else if (this.view_mode === 'list') {
                const selectedAssetIds = Array.isArray(this.selectedAssetIds) ? this.selectedAssetIds : [];
                if (selectedAssetIds.length === 0) return;
                
                Swal.fire({
                    title: `Are you sure you want to delete ${selectedAssetIds.length} touchpoint${selectedAssetIds.length === 1 ? '' : 's'}?`,
                    text: "This action cannot be undone.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, delete",
                    customClass: {
                        popup: 'swal-warning'
                    },
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        try {
                            const idsToDelete = [...selectedAssetIds];
                            await Promise.all(idsToDelete.map(async (id) => {
                                const touchpoint = this.playbook.assets.find(t => t.id === id);
                                if (touchpoint) {
                                    await window.axios.delete(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}`);
                                    this.playbookStore.removeAsset(touchpoint.id);
                                }
                            }));

                            // Clear selection and notify
                            this.$emit('cards-state-changed', []);

                            Swal.fire({
                                title: "Deleted!",
                                text: "The touchpoints have been deleted.",
                                icon: "success",
                                customClass: {
                                    popup: 'swal-success'
                                },
                            });
                        } catch (error) {
                            console.error('Error deleting selected touchpoints:', error);
                            this.showNotification({
                                text: 'Failed to delete some touchpoints',
                                type: 'error'
                            });
                        }
                    }
                });
            }
        },

        importContent() {
            // Show the modal using Bootstrap's modal API
            const modal = new bootstrap.Modal(document.getElementById('importHistoryModal'));
            modal.show();
        },

        refreshHistory(history_id) {
            // Find the history in the local_exports array and update it
            const history = this.local_exports.find(history => history.id === history_id);
            if (history) {
                history.imported_at = new Date();
                this.$forceUpdate();
            }
        },

        enrichExportsWithAssetTypes(exports) {
            // Enrich exports with asset_type information from current playbook assets
            if (!exports || !Array.isArray(exports) || !this.playbook?.assets) {
                return exports;
            }

            return exports.map(exportHistory => {
                if (!exportHistory.assets || !Array.isArray(exportHistory.assets)) {
                    return exportHistory;
                }

                const enrichedAssets = exportHistory.assets.map(asset => {
                    // If asset_type is already present, keep it
                    if (asset.asset_type) {
                        return asset;
                    }

                    // Otherwise, look it up from current playbook assets
                    const currentAsset = this.playbook.assets.find(a => a.id === asset.id);
                    if (currentAsset && currentAsset.asset_type) {
                        return {
                            ...asset,
                            asset_type: {
                                id: currentAsset.asset_type.id,
                                name: currentAsset.asset_type.name,
                                icon_class: currentAsset.asset_type.icon_class
                            }
                        };
                    }

                    return asset;
                });

                return {
                    ...exportHistory,
                    assets: enrichedAssets
                };
            });
        },

        formatStatus(status) {
            if (!status) return '';
            // Convert snake_case or kebab-case to Title Case
            return status
                .replace(/_/g, ' ')
                .replace(/-/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        },

        handleFilterChange() {
            this.applyFilters();
        },

        setGroupBy(option) {
            this.groupBy = option === this.groupBy ? '' : option;
            // Close the dropdown
            this.$nextTick(() => {
                if (this.$refs.groupByButton) {
                    const dropdown = bootstrap.Dropdown.getInstance(this.$refs.groupByButton);
                    if (dropdown) {
                        dropdown.hide();
                    }
                }
            });
            // Apply grouping logic here if needed
            this.applyFilters();
        },

        handleSearchInput: debounce(function() {
            this.applyFilters();
        }, 300),

        applyFilters() {
            if (!this.playbook?.assets) {
                this.filteredTouchpoints = [];
                return;
            }

            let filtered = [...this.playbook.assets];

            // Filter by search term
            if (this.filters.search) {
                const searchLower = this.filters.search.toLowerCase();
                filtered = filtered.filter(asset => {
                    return asset.name?.toLowerCase().includes(searchLower);
                });
            }

            // Filter by status
            if (this.filters.status) {
                filtered = filtered.filter(asset => {
                    // Use the same calculated status logic as in TouchpointsGridView
                    let calculatedStatus;
                    if (asset.deployed_at) {
                        calculatedStatus = 'Launched';
                    } else if (asset.status == 'approved') {
                        calculatedStatus = 'Approved';
                    } else if (asset.status == 'generating') {
                        calculatedStatus = 'Generating';
                    } else if (asset.source == null) {
                        calculatedStatus = 'Incomplete';
                    } else {
                        calculatedStatus = 'Ready';
                    }
                    return calculatedStatus.toLowerCase() === this.filters.status.toLowerCase();
                });
            }

            // Filter by asset type (array of selected types)
            if (Array.isArray(this.filters.asset_type) && this.filters.asset_type.length > 0) {
                // Convert filter IDs to strings for comparison
                const selectedIds = this.filters.asset_type.map(id => String(id));
                filtered = filtered.filter(asset => {
                    return asset.asset_type && selectedIds.includes(String(asset.asset_type.id));
                });
            }

            // Filter by audience
            if (this.filters.audience) {
                filtered = filtered.filter(asset => {
                    return asset.segment?.id === this.filters.audience;
                });
            }

            this.filteredTouchpoints = filtered;
            
            // Deselect any assets that are no longer in the filtered list
            if (Array.isArray(this.selectedAssetIds) && this.selectedAssetIds.length > 0) {
                const filteredIds = filtered.map(asset => asset.id);
                const selectedButFilteredOut = this.selectedAssetIds.filter(id => !filteredIds.includes(id));
                
                if (selectedButFilteredOut.length > 0) {
                    // Remove filtered-out assets from selection
                    const updatedSelection = this.selectedAssetIds.filter(id => filteredIds.includes(id));
                    this.$emit('cards-state-changed', updatedSelection);
                }
            }
            
            // Apply sorting if in list view
            if (this.view_mode === 'list') {
                this.applySorting();
            }
        },
        
        updateCardsState(ids) {
            // Increment key to force computed properties to re-evaluate
            this.cardsStateKey++;
            // Bubble current selection up so Launch button can reflect count
            // Grid and list views support selection
            if (this.view_mode === 'grid') {
                const selected = Array.isArray(ids) ? ids : (this.$refs.gridView?.selectedCards || []);
                this.$emit('cards-state-changed', selected);
            } else if (this.view_mode === 'list') {
                const selected = Array.isArray(ids) ? ids : [];
                this.$emit('cards-state-changed', selected);
            }
        },
        
        setViewMode(mode) {
            this.view_mode = mode;
            // Clear selected touchpoints when switching to calendar view
            if (mode === 'calendar') {
                this.$emit('cards-state-changed', []);
                
                // If panel is open, re-apply the height constraint
                if (this.showTouchpointsPanel) {
                    this.$nextTick(() => {
                        // Update calendar size first
                        if (this.$refs.calendarView) {
                            this.$refs.calendarView.updateCalendarSize();
                        }
                        // Then match panel height
                        setTimeout(() => {
                            this.matchPanelHeightToCalendar();
                        }, 100);
                    });
                }
            }
            // Only apply sorting when switching to list view if user has explicitly set a sort
            // Otherwise, preserve the primary-first ordering from grid view
            if (mode === 'list' && this.sort_key !== '') {
                this.$nextTick(() => {
                    this.applySorting();
                });
            }
        },
        
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = (this.sort_order === 'asc') ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }
            // Always apply sorting when user explicitly clicks a column header
            this.applySorting();
        },
        
        applySorting() {
            if (!this.filteredTouchpoints || this.filteredTouchpoints.length === 0) return;
            
            // If no explicit sort is set, don't sort
            if (this.sort_key === '') {
                return;
            }
            
            const sorted = [...this.filteredTouchpoints];
            
            sorted.sort((a, b) => {
                // Apply the user's selected sort
                let aValue, bValue;
                
                switch (this.sort_key) {
                    case 'name':
                        aValue = (a.name || '').toLowerCase();
                        bValue = (b.name || '').toLowerCase();
                        break;
                    case 'asset_type':
                        // Sort by asset type display name, not ID
                        aValue = (a.asset_type?.name || 'zzz').toLowerCase(); // 'zzz' ensures items without type sort last
                        bValue = (b.asset_type?.name || 'zzz').toLowerCase();
                        break;
                    case 'launch_date':
                        aValue = a.launch_date ? new Date(a.launch_date).getTime() : 0;
                        bValue = b.launch_date ? new Date(b.launch_date).getTime() : 0;
                        break;
                    case 'audience':
                        // Sort by audience/segment display name, not ID
                        aValue = (a.segment?.name || 'zzz').toLowerCase(); // 'zzz' ensures items without audience sort last
                        bValue = (b.segment?.name || 'zzz').toLowerCase();
                        break;
                    default:
                        return 0;
                }
                
                if (aValue < bValue) return this.sort_order === 'asc' ? -1 : 1;
                if (aValue > bValue) return this.sort_order === 'asc' ? 1 : -1;
                return 0;
            });
            
            this.filteredTouchpoints = sorted;
        },
        
        handleCalendarUpdate(updateData) {
            // Handle updates from calendar drag-and-drop
            // CalendarView handles the API call, we just update the store
            if (!updateData || !updateData.assetId) return;
            
            // Update the asset in the store
            this.playbookStore.updateAsset(updateData.assetId, { 
                launch_date: updateData.launchDate 
            });
        },
        
        initializeAssetTypeFilter() {
            // Initialize asset_type filter to all available types (default: all selected)
            // Only initialize if user hasn't interacted with the filter yet
            if (!this.assetTypeFilterUserInteracted && this.availableAssetTypes.length > 0 && (!this.filters.asset_type || this.filters.asset_type.length === 0)) {
                // Set to all asset type IDs
                this.filters.asset_type = this.availableAssetTypes.map(at => String(at.id));
                // Sync legend when initializing
                this.syncLegendFilters();
            }
        },
        
        handleAssetTypeFilterUpdate(selectedOptions) {
            // Handle filter update from AccordionMultiSelect
            // selectedOptions is an array of option objects: [{value, text, category, subcategory}, ...]
            if (!selectedOptions || !Array.isArray(selectedOptions)) {
                return;
            }
            
            // Mark that user has interacted with the filter
            this.assetTypeFilterUserInteracted = true;
            
            // Extract IDs from selected option objects
            const filterValue = selectedOptions
                .map(option => {
                    // Option can be an object with value property, or just a value string/number
                    const id = typeof option === 'object' && option !== null ? option.value : option;
                    return id ? String(id) : null;
                })
                .filter(id => id !== null);
            
            // If user deselected all, reset to all selected (default behavior)
            if (filterValue.length === 0 && this.availableAssetTypes.length > 0) {
                this.filters.asset_type = this.availableAssetTypes.map(at => String(at.id));
                // Reset the interaction flag so it can be initialized again if needed
                this.assetTypeFilterUserInteracted = false;
            } else {
                this.filters.asset_type = filterValue;
            }
            
            // Update calendar legend filters to sync (inverse of dropdown selection)
            this.syncLegendFilters();
            this.handleFilterChange();
        },
        
        syncLegendFilters() {
            // Sync legend filters with dropdown filter
            // Legend should show strike-through for types NOT in dropdown selection
            // So calendarLegendFilters should be empty (legend is synced via isAssetTypeFiltered)
            // We keep calendarLegendFilters for backward compatibility but it's not used for filtering anymore
            this.calendarLegendFilters = [];
        },
        
        handleAssetTypeFilterChange(filterArray) {
            // Update the calendar legend filters array when clicking on legend items
            // This is kept for backward compatibility but may not be used if syncing with dropdown
            this.calendarLegendFilters = Array.isArray(filterArray) ? filterArray : [];
        },
        
        handleDropdownFilterUpdate(newFilterArray) {
            // Handle dropdown filter update from legend clicks
            // Mark that user has interacted with the filter
            this.assetTypeFilterUserInteracted = true;
            
            // Convert to array of strings
            const filterValue = Array.isArray(newFilterArray) 
                ? newFilterArray.map(id => String(id))
                : [];
            
            // If user deselected all via legend, reset to all selected (default behavior)
            if (filterValue.length === 0 && this.availableAssetTypes.length > 0) {
                this.filters.asset_type = this.availableAssetTypes.map(at => String(at.id));
                // Reset the interaction flag so it can be initialized again if needed
                this.assetTypeFilterUserInteracted = false;
            } else {
                this.filters.asset_type = filterValue;
            }
            
            // Sync legend
            this.syncLegendFilters();
            
            // Trigger filter change to update the view
            this.handleFilterChange();
        },
        openCreateModal() {
            this.createModal.show = true;
        },
        closeCreateModal() {
            this.createModal.show = false;
        },
        handleCreateTouchpoint(asset) {
            // Handle the created touchpoint from the modal
            // Add the new asset to the store
            this.playbookStore.addAsset(asset);
            // Refresh filtered list so grid/table/calendar show the new touchpoint
            this.applyFilters();

            // Open edit modal for the newly created touchpoint
            this.$nextTick(() => {
                if (this.view_mode === 'grid' && this.$refs.gridView) {
                    this.$refs.gridView.editTouchpoint(asset);
                } else if (this.view_mode === 'list' && this.$refs.listView) {
                    this.$refs.listView.editTouchpoint(asset);
                }
            });
        },
        
        // Playbook editing methods
        startEditName() {
            if (!this.canEdit('playbooks')) return;
            this.tempName = this.playbook.name;
            this.editingName = true;
            this.$nextTick(() => {
                if (this.$refs.nameInput) {
                    this.$refs.nameInput.focus();
                    this.$refs.nameInput.select();
                }
            });
        },
        
        cancelEditName() {
            this.editingName = false;
            this.tempName = '';
        },
        
        saveName() {
            if (!this.editingName || this.tempName === this.playbook.name) {
                this.cancelEditName();
                return;
            }
            
            this.savingName = true;
            const payload = {
                name: this.tempName,
                description: this.playbook.description,
                status: this.playbook.status
            };
            
            // Include dates if they exist to prevent null constraint violations
            if (this.playbook.start_date) {
                payload.start_date = this.playbook.start_date;
            }
            if (this.playbook.end_date) {
                payload.end_date = this.playbook.end_date;
            }
            
            // Include prompts and audiences to prevent them from being cleared
            if (this.playbook.prompts) {
                payload.prompts = this.playbook.prompts;
            }
            if (this.playbook.audiences) {
                payload.audiences = this.playbook.audiences;
            }
            
            window.axios.put(`/api/playbooks/${this.client.url}/${this.playbook.url}`, payload)
                .then(response => {
                    if (response.data && response.data.playbook) {
                        this.playbookStore.updateSelectedPlaybook(response.data.playbook);
                    }
                    this.editingName = false;
                    
                    // Show success toast
                    this.showNotification({
                        text: '<strong>' + response.data.playbook.name + '</strong> was successfully updated!'
                    });
                })
                .catch(error => {
                    Swal.fire({
                        title: 'Failed to update',
                        text: error.response?.data?.error || 'An error occurred while updating the playbook name',
                        icon: 'error',
                        customClass: { popup: 'swal-error' }
                    });
                    this.cancelEditName();
                })
                .finally(() => {
                    this.savingName = false;
                });
        },
        
        startEditDescription() {
            if (!this.canEdit('playbooks')) return;
            this.tempDescription = this.playbook.description || '';
            this.editingDescription = true;
            this.showFullDescription = false; // Reset to truncated view when editing
            this.$nextTick(() => {
                if (this.$refs.descriptionTextarea) {
                    this.$refs.descriptionTextarea.focus();
                }
            });
        },
        
        cancelEditDescription() {
            this.editingDescription = false;
            this.tempDescription = '';
        },
        
        saveDescription() {
            if (!this.editingDescription || this.tempDescription === (this.playbook.description || '')) {
                this.cancelEditDescription();
                return;
            }
            
            this.savingDescription = true;
            const payload = {
                name: this.playbook.name,
                description: this.tempDescription,
                status: this.playbook.status
            };
            
            // Include dates if they exist to prevent null constraint violations
            if (this.playbook.start_date) {
                payload.start_date = this.playbook.start_date;
            }
            if (this.playbook.end_date) {
                payload.end_date = this.playbook.end_date;
            }
            
            // Include prompts and audiences to prevent them from being cleared
            if (this.playbook.prompts) {
                payload.prompts = this.playbook.prompts;
            }
            if (this.playbook.audiences) {
                payload.audiences = this.playbook.audiences;
            }
            
            window.axios.put(`/api/playbooks/${this.client.url}/${this.playbook.url}`, payload)
                .then(response => {
                    if (response.data && response.data.playbook) {
                        this.playbookStore.updateSelectedPlaybook(response.data.playbook);
                    }
                    this.editingDescription = false;
                    
                    // Show success toast
                    this.showNotification({
                        text: '<strong>' + response.data.playbook.name + "</strong> was successfully updated!"
                    });
                })
                .catch(error => {
                    Swal.fire({
                        title: 'Failed to update',
                        text: error.response?.data?.error || 'An error occurred while updating the playbook description',
                        icon: 'error',
                        customClass: { popup: 'swal-error' }
                    });
                    this.cancelEditDescription();
                })
                .finally(() => {
                    this.savingDescription = false;
                });
        },
        
        startEditStartDate() {
            if (!this.canEdit('playbooks')) return;
            this.originalStartDate = this.playbook.start_date ? new Date(this.playbook.start_date) : null;
            this.tempStartDate = this.originalStartDate;
            this.editingStartDate = true;
        },
        
        handleStartDateChange(newDate) {
            // DatepickerUtc emits a Date object that's already in UTC
            // Save after a short delay to allow the datepicker to close
            this.$nextTick(() => {
                this.saveStartDate(newDate);
            });
        },
        
        saveStartDate(newDate) {
            if (!this.editingStartDate) return;
            
            // If the date was cleared (null), reset to original value and cancel editing
            if (!newDate) {
                this.tempStartDate = this.originalStartDate;
                this.editingStartDate = false;
                return;
            }
            
            // Check if date actually changed by comparing date strings
            // The playbook date is in UTC, and newDate from DatepickerUtc is also in UTC
            const currentDate = this.playbook.start_date ? new Date(this.playbook.start_date).toISOString().split('T')[0] : null;
            const newDateStr = newDate ? new Date(newDate).toISOString().split('T')[0] : null;
            
            if (currentDate === newDateStr) {
                this.editingStartDate = false;
                return;
            }
            
            this.savingDate = 'start';
            // DatepickerUtc already converts to UTC, so we just need to extract the date part (YYYY-MM-DD)
            const payload = {
                name: this.playbook.name,
                description: this.playbook.description,
                status: this.playbook.status,
                change_type: 'start',
                change_date: newDate ? new Date(newDate).toISOString().split('T')[0] : null
            };
            
            // Include prompts and audiences to prevent them from being cleared
            if (this.playbook.prompts) {
                payload.prompts = this.playbook.prompts;
            }
            if (this.playbook.audiences) {
                payload.audiences = this.playbook.audiences;
            }
            
            window.axios.put(`/api/playbooks/${this.client.url}/${this.playbook.url}`, payload)
                .then(response => {
                    if (response.data && response.data.playbook) {
                        // Backend already updates asset launch dates, so just update the playbook
                        this.playbookStore.updateSelectedPlaybook(response.data.playbook);
                    }
                    
                    this.editingStartDate = false;
                    
                    // Show success toast
                    this.showNotification({
                        text: '<strong>' + response.data.playbook.name + '</strong> was successfully updated!'
                    });
                })
                .catch(error => {
                    Swal.fire({
                        title: 'Failed to update',
                        text: error.response?.data?.error || 'An error occurred while updating the start date',
                        icon: 'error',
                        customClass: { popup: 'swal-error' }
                    });
                    this.editingStartDate = false;
                })
                .finally(() => {
                    this.savingDate = null;
                });
        },
        
        startEditEndDate() {
            if (!this.canEdit('playbooks')) return;
            this.originalEndDate = this.playbook.end_date ? new Date(this.playbook.end_date) : null;
            this.tempEndDate = this.originalEndDate;
            this.editingEndDate = true;
        },
        
        handleEndDateChange(newDate) {
            // DatepickerUtc emits a Date object that's already in UTC
            // Save after a short delay to allow the datepicker to close
            this.$nextTick(() => {
                this.saveEndDate(newDate);
            });
        },
        
        saveEndDate(newDate) {
            if (!this.editingEndDate) return;
            
            // If the date was cleared (null), reset to original value and cancel editing
            if (!newDate) {
                this.tempEndDate = this.originalEndDate;
                this.editingEndDate = false;
                return;
            }
            
            // Check if date actually changed by comparing date strings
            // The playbook date is in UTC, and newDate from DatepickerUtc is also in UTC
            const currentDate = this.playbook.end_date ? new Date(this.playbook.end_date).toISOString().split('T')[0] : null;
            const newDateStr = newDate ? new Date(newDate).toISOString().split('T')[0] : null;
            
            if (currentDate === newDateStr) {
                this.editingEndDate = false;
                return;
            }
            
            this.savingDate = 'end';
            // DatepickerUtc already converts to UTC, so we just need to extract the date part (YYYY-MM-DD)
            const payload = {
                name: this.playbook.name,
                description: this.playbook.description,
                status: this.playbook.status,
                change_type: 'end',
                change_date: newDate ? new Date(newDate).toISOString().split('T')[0] : null
            };
            
            // Include prompts and audiences to prevent them from being cleared
            if (this.playbook.prompts) {
                payload.prompts = this.playbook.prompts;
            }
            if (this.playbook.audiences) {
                payload.audiences = this.playbook.audiences;
            }
            
            window.axios.put(`/api/playbooks/${this.client.url}/${this.playbook.url}`, payload)
                .then(response => {
                    if (response.data && response.data.playbook) {
                        // Backend already updates asset launch dates, so just update the playbook
                        this.playbookStore.updateSelectedPlaybook(response.data.playbook);
                    }
                    
                    this.editingEndDate = false;
                    
                    // Show success toast
                    this.showNotification({
                        text: '<strong>' + response.data.playbook.name + '</strong> was successfully updated!'
                    });
                })
                .catch(error => {
                    Swal.fire({
                        title: 'Failed to update',
                        text: error.response?.data?.error || 'An error occurred while updating the end date',
                        icon: 'error',
                        customClass: { popup: 'swal-error' }
                    });
                    this.editingEndDate = false;
                })
                .finally(() => {
                    this.savingDate = null;
                });
        },
        
        // Format date to match DatepickerUtc display format (M/d/yyyy)
        // This ensures the date shown in the span matches what the datepicker displays
        // Uses the same timezone adjustment as DatepickerUtc to preserve the date value
        formatDateForDisplay(dateValue) {
            if (!dateValue) return '';
            
            // Parse the date (handles both Date objects and ISO strings)
            const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
            
            // Adjust for timezone offset to preserve the date value (same as DatepickerUtc)
            // This ensures UTC dates like "2025-11-03T00:00:00.000000Z" display as 11/3/2025
            // regardless of the user's timezone
            const offset = date.getTimezoneOffset();
            date.setMinutes(date.getMinutes() + offset);
            
            // Format as M/d/yyyy to match DatepickerUtc's inputFormat (no leading zeros)
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            
            return `${month}/${day}/${year}`;
        },
        
        toggleDescription() {
            this.showFullDescription = !this.showFullDescription;
        },
        
    },
    
    mounted() {
        // Initialize filtered touchpoints
        this.filteredTouchpoints = this.playbook?.assets || [];
        
        // Initialize asset_type filter to all available types (default: all selected)
        this.initializeAssetTypeFilter();
        
        // Initialize exports from props and enrich with asset_type info
        this.local_exports = this.enrichExportsWithAssetTypes([...this.exports]);
        
        // Initialize Bootstrap tooltips
        this.$nextTick(() => {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            tooltipTriggerList.map(function (tooltipTriggerEl) {
                return new bootstrap.Tooltip(tooltipTriggerEl);
            });
        });
    },
    
    watch: {
        playbook: {
            handler() {
                this.applyFilters();
                // Reset interaction flag when playbook changes
                this.assetTypeFilterUserInteracted = false;
                // Reset description view when playbook changes
                this.showFullDescription = false;
                // Initialize asset type filter when playbook changes
                this.initializeAssetTypeFilter();
                // Re-enrich exports when playbook changes (assets might have been updated)
                this.local_exports = this.enrichExportsWithAssetTypes([...this.exports]);
            },
            deep: true,
            immediate: false
        },
        availableAssetTypes: {
            handler() {
                // Only initialize if user hasn't interacted yet
                if (!this.assetTypeFilterUserInteracted) {
                    this.initializeAssetTypeFilter();
                }
            },
            immediate: false
        },
        exports: {
            handler(newVal) {
                // Enrich exports with asset_type info when exports prop changes
                this.local_exports = this.enrichExportsWithAssetTypes([...newVal]);
            },
            deep: true
        },
        view_mode: {
            handler(newMode, oldMode) {
                // When switching to grid view, ensure the grid view syncs its selection
                if (newMode === 'grid' && this.$refs.gridView && Array.isArray(this.selectedAssetIds) && this.selectedAssetIds.length > 0) {
                    this.$nextTick(() => {
                        if (this.$refs.gridView) {
                            this.$refs.gridView.selectedCards = [...this.selectedAssetIds];
                        }
                    });
                }
            }
        }
    }
}
</script>